import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityItem } from './ActivityItem';
import { activityLogService } from '@/lib/api/activity-log.service';
import { ActivityLog, ActivityLogFilters } from '@/types/activity-log.types';
import { Loader2, AlertCircle } from 'lucide-react';
import { Session } from '@om/shared-auth';

interface ActivityFeedProps {
  celebrationId?: number;
  showUserActivities?: boolean;
  filters?: ActivityLogFilters;
  pageSize?: number;
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

/**
 * ActivityFeed Component
 * Displays a paginated list of activity log entries
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  celebrationId,
  showUserActivities = false,
  filters,
  pageSize = 20,
  autoRefresh = false,
  refreshInterval = 30000, // 30 seconds default
}) => {
  const { t } = useTranslation('common');
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const filtersKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);

  const checkSessionActive = async (): Promise<boolean> => {
    try {
      return await Session.doesSessionExist();
    } catch {
      return true;
    }
  };

  const fetchActivities = async (reset: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const currentOffset = reset ? 0 : offset;
      let result;

      if (celebrationId) {
        result = await activityLogService.getCelebrationActivities(
          celebrationId,
          filters,
          pageSize,
          currentOffset
        );
      } else if (showUserActivities) {
        result = await activityLogService.getUserActivities(
          filters,
          pageSize,
          currentOffset
        );
      } else {
        result = await activityLogService.getActivities(
          filters,
          pageSize,
          currentOffset
        );
      }

      if (reset) {
        setActivities(result.data);
        setOffset(pageSize);
      } else {
        setActivities((prev) => [...prev, ...result.data]);
        setOffset((prev) => prev + pageSize);
      }

      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(t('activityFeed.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runInitialLoad = async () => {
      const doesSessionExist = await checkSessionActive();

      if (!isMounted) {
        return;
      }

      if (!doesSessionExist) {
        setActivities([]);
        setLoading(false);
        setError(null);
        return;
      }

      fetchActivities(true);
    };

    void runInitialLoad();

    return () => {
      isMounted = false;
    };
  }, [celebrationId, showUserActivities, filtersKey]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const intervalId = setInterval(() => {
      // Silently refresh without showing loading state
      const fetchSilently = async () => {
        try {
          const doesSessionExist = await checkSessionActive();
          if (!doesSessionExist) {
            return;
          }

          let result;

          if (celebrationId) {
            result = await activityLogService.getCelebrationActivities(
              celebrationId,
              filters,
              pageSize,
              0
            );
          } else if (showUserActivities) {
            result = await activityLogService.getUserActivities(
              filters,
              pageSize,
              0
            );
          } else {
            result = await activityLogService.getActivities(
              filters,
              pageSize,
              0
            );
          }

          setActivities(result.data);
          setOffset(pageSize);
          setHasMore(result.hasMore);
        } catch (err) {
          console.error('Error during auto-refresh:', err);
          // Silently fail - don't update error state during auto-refresh
        }
      };

      fetchSilently();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, celebrationId, showUserActivities, filtersKey, pageSize]);

  const handleLoadMore = () => {
    fetchActivities(false);
  };

  const handleRetry = () => {
    fetchActivities(true);
  };

  const handleEntityClick = () => {
    // Custom entity click handler can be implemented here
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        <span className="ml-2 text-[11px] text-gray-600">{t('activityFeed.loading')}</span>
      </div>
    );
  }

  if (error && activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6">
        <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
        <p className="text-[11px] text-gray-700 mb-2">{error}</p>
        <button
          onClick={handleRetry}
          className="px-3 py-1.5 text-[11px] bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-gray-500">
        <p className="text-xs font-medium">{t('activityFeed.empty.title')}</p>
      <p className="text-[10px] mt-1">{t('activityFeed.empty.description')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Activity List */}
      <div className="divide-y divide-gray-100">
        {activities.map((activity) => (
          <ActivityItem
            key={activity.id}
            activity={activity}
            onEntityClick={handleEntityClick}
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="w-full px-3 py-1.5 text-[11px] bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                {t('activityFeed.loadingMore')}
              </>
            ) : (
              t('activityFeed.loadMore')
            )}
          </button>
        </div>
      )}
    </div>
  );
};
